import { useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Control } from "react-hook-form";

import type { TalentMeetingDetails } from "@aglocal/schema/TalentMeetingDetails";

import { Span } from "@aglocal/web/components/Typography";

import { UseFormSetDefault } from "@/hooks/useSetDefault";

import { PostMeetingFormWithSummary } from "../form";
import { FIELD_LABELS } from "../messages";

import StringField from "./StringField";

export interface CompanyFieldProps {
  currentValue?: string | null;
  details: Readonly<TalentMeetingDetails> | undefined;
  control: Control<PostMeetingFormWithSummary>;
  setDefault: UseFormSetDefault<PostMeetingFormWithSummary>;
}

export default function CompanyField({
  currentValue,
  details,
  control,
  setDefault,
}: CompanyFieldProps) {
  const { $t } = useIntl();
  const defaultValue = useMemo(() => {
    if (details?.currentCompany) return details.currentCompany;
    return currentValue ?? "";
  }, [details?.currentCompany, currentValue]);

  useEffect(() => {
    setDefault("currentCompany", defaultValue);
  }, [defaultValue, setDefault]);

  return (
    <StringField
      name="currentCompany"
      label={$t(FIELD_LABELS.currentCompany)}
      control={control}
      hint={
        currentValue && (
          <FormattedMessage
            defaultMessage="Current Company: <b>{company}</b>"
            description="Hint under the current company field showing the company currently on the talent's Connected profile; {company} is that company."
            values={{
              company: currentValue,
              b: (chunks) => (
                <Span size={200} weight="bold">
                  {chunks}
                </Span>
              ),
            }}
          />
        )
      }
    />
  );
}
